import {FC, useCallback, useEffect, useState} from 'react'
import {Tabs, Tab} from 'react-bootstrap'
import {getLogger} from '../../../../shared/utils'
import {CloudLogsTabContent} from './JobCloudLogsPanel'
import {fetchSessionLogs, formatProgressMessages} from '../core/jobLogs'

/** Events of the session's own stream the Job Messages tab shows (the oldest ones). */
const MESSAGES_LIMIT = 100

type LogTabType = 'messages' | 'logs'

const LOGS_STATE_KEY = 'jobs-logs-state'

interface LogTab {
  key: string
  title: string
  sessionId: string
  type: LogTabType
  job: Record<string, unknown>
}

interface PersistedLogsState {
  tabs: Array<Omit<LogTab, 'job'>>
  activeTab: string
  cachedContent: Record<string, string>
}

function loadLogsState(): PersistedLogsState | null {
  try {
    const raw = sessionStorage.getItem(LOGS_STATE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    /* ignore corrupt data */
  }
  return null
}

function saveLogsState(tabs: LogTab[], activeTab: string, cachedContent: Record<string, string>) {
  try {
    const persistableTabs = tabs.map(({key, title, sessionId, type}) => ({
      key,
      title,
      sessionId,
      type,
    }))
    sessionStorage.setItem(
      LOGS_STATE_KEY,
      JSON.stringify({tabs: persistableTabs, activeTab, cachedContent})
    )
  } catch {
    /* storage full or unavailable */
  }
}

/**
 * The job's progress messages: the session's own stream, one `[time] message` line per event.
 * Step streams belong to the Session Logs tab, so none are requested (`limit: 0`).
 */
async function fetchProgressMessages(sessionId: string): Promise<string> {
  const logs = await fetchSessionLogs(sessionId, {limit: 0, sessionLimit: MESSAGES_LIMIT})
  if (!logs.found) return 'No logs available for this session.'
  if (logs.sessionEvents.length === 0) return 'No log events found for this session.'
  return formatProgressMessages(logs.sessionEvents)
}

function buildTabKey(type: LogTabType, sessionId: string): string {
  return `${type}-${sessionId}`
}

function buildTabTitle(type: LogTabType, jobLabel: string): string {
  return type === 'messages' ? `Job Messages — ${jobLabel}` : `Session Logs — ${jobLabel}`
}

interface JobLogsPanelProps {
  job: Record<string, unknown>
  tabType: LogTabType
  onClose: () => void
}

const JobLogsPanel: FC<JobLogsPanelProps> = ({job, tabType}) => {
  const sessionId = (job?.SessionId as string) || ''
  const jobLabel = (job?.customId as string) || sessionId || '—'

  const persisted = loadLogsState()

  const defaultTab: LogTab = {
    key: buildTabKey(tabType, sessionId),
    title: buildTabTitle(tabType, jobLabel),
    sessionId,
    type: tabType,
    job,
  }

  const [tabs, setTabs] = useState<LogTab[]>(() => {
    if (persisted?.tabs?.length) {
      return persisted.tabs.map((pt) => ({
        ...pt,
        job: pt.sessionId === sessionId ? job : {runState: 'COMPLETE'},
      }))
    }
    return [defaultTab]
  })

  const [activeTab, setActiveTab] = useState<string>(persisted?.activeTab || defaultTab.key)

  const [cachedContent, setCachedContent] = useState<Record<string, string>>(
    persisted?.cachedContent ?? {}
  )

  const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({})

  useEffect(() => {
    saveLogsState(tabs, activeTab, cachedContent)
  }, [tabs, activeTab, cachedContent])

  const ensureTabExists = useCallback(() => {
    const tabKey = buildTabKey(tabType, sessionId)
    setTabs((prev) => {
      const existing = prev.find((t) => t.key === tabKey)
      if (existing) {
        if (existing.job !== job) {
          return prev.map((t) => (t.key === tabKey ? {...t, job} : t))
        }
        return prev
      }
      return [
        ...prev,
        {
          key: tabKey,
          title: buildTabTitle(tabType, jobLabel),
          sessionId,
          type: tabType,
          job,
        },
      ]
    })
    setActiveTab(tabKey)
  }, [sessionId, jobLabel, tabType, job])

  useEffect(() => {
    if (sessionId) ensureTabExists()
  }, [sessionId, tabType, ensureTabExists])

  const loadMessagesForTab = useCallback(
    async (tab: LogTab) => {
      if (cachedContent[tab.key]) return

      setLoadingTabs((prev) => ({...prev, [tab.key]: true}))
      try {
        const logs = await fetchProgressMessages(tab.sessionId)
        setCachedContent((prev) => ({...prev, [tab.key]: logs}))
      } catch (error) {
        getLogger().error('Error fetching messages:', error)
        setCachedContent((prev) => ({
          ...prev,
          [tab.key]: 'No messages available for this analysis.',
        }))
      } finally {
        setLoadingTabs((prev) => ({...prev, [tab.key]: false}))
      }
    },
    [cachedContent]
  )

  useEffect(() => {
    const currentTab = tabs.find((t) => t.key === activeTab)
    if (currentTab && currentTab.type === 'messages' && !cachedContent[currentTab.key]) {
      loadMessagesForTab(currentTab)
    }
  }, [activeTab, tabs, cachedContent, loadMessagesForTab])

  const handleTabSelect = useCallback((key: string | null) => {
    if (key) setActiveTab(key)
  }, [])

  const handleCloseTab = useCallback(
    (tabKey: string, e: React.MouseEvent) => {
      e.stopPropagation()
      setTabs((prev) => {
        const updated = prev.filter((t) => t.key !== tabKey)
        if (updated.length === 0) return prev
        return updated
      })
      setCachedContent((prev) => {
        const next = {...prev}
        delete next[tabKey]
        return next
      })
      setActiveTab((prev) => {
        if (prev !== tabKey) return prev
        const remaining = tabs.filter((t) => t.key !== tabKey)
        return remaining.length > 0 ? remaining[remaining.length - 1].key : ''
      })
    },
    [tabs]
  )

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
        className='ob-jobs__log-tabs mb-0'
        mountOnEnter
        unmountOnExit={false}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            eventKey={tab.key}
            title={
              <span style={{display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                {tab.title}
                {tabs.length > 1 && (
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={(e) => handleCloseTab(tab.key, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')
                        handleCloseTab(tab.key, e as unknown as React.MouseEvent)
                    }}
                    title='Close tab'
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      fontSize: 'var(--ob-font-size-xs)',
                      lineHeight: 1,
                      color: 'var(--ob-text-secondary)',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--ob-gray-300)'
                      e.currentTarget.style.color = 'var(--ob-text-primary)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = 'var(--ob-text-secondary)'
                    }}
                  >
                    &times;
                  </span>
                )}
              </span>
            }
          >
            {tab.type === 'messages' ? (
              <div style={{flex: 1, overflow: 'auto', padding: 'var(--ob-spacing-4, 1rem)'}}>
                {loadingTabs[tab.key] ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '40px',
                      minHeight: '200px',
                    }}
                  >
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--ob-font-size-lg)',
                        color: 'var(--ob-text-secondary)',
                      }}
                    >
                      Loading job messages...
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--ob-font-size-base)',
                        color: 'var(--ob-text-muted)',
                        marginTop: '8px',
                      }}
                    >
                      This may take a few moments
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      whiteSpace: 'pre-line',
                      fontFamily: 'var(--ob-font-mono)',
                      fontSize: 'var(--ob-font-size-sm)',
                      backgroundColor: 'var(--ob-gray-50)',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid var(--ob-gray-300)',
                      overflow: 'auto',
                      lineHeight: '1.4',
                    }}
                  >
                    {cachedContent[tab.key] || 'No messages available'}
                  </div>
                )}
              </div>
            ) : (
              <CloudLogsTabContent sessionId={tab.sessionId} job={tab.job} />
            )}
          </Tab>
        ))}
      </Tabs>
    </div>
  )
}

function clearLogsState() {
  try {
    sessionStorage.removeItem(LOGS_STATE_KEY)
  } catch {
    /* ignore */
  }
}

export {JobLogsPanel, clearLogsState}
export type {LogTabType}
